import { type Reducer, useReducer } from 'react';
import { type SortingOrder } from '../api/types';
import { type TableSortingOrder } from '../utilities/useSortAndFilterCache';
import { type ExtendedPageState, type ExtendedPageAction, extendedReducer } from './PaginatedTableWithContext';

export type SortingRule<SortField> = {
    field: SortField;
    order: SortingOrder;
    orderValue: TableSortingOrder | undefined;
};

export type FilteringRule<FilterField> = {
    [key in keyof FilterField]: FilterField[key] | undefined;
};

export type State<SortField, FilterField> = ExtendedPageState & {
    sort?: SortingRule<SortField>;
    filter?: FilteringRule<FilterField>;
};

export type SetSortAction<SortField> = { type: 'setSort'; sortBy: SortingRule<SortField> };
export type SetFilterAction<FilterField> = { type: 'setFilter'; filteringRule: FilteringRule<FilterField> };

export type Action<SortField, FilterField> =
    | ExtendedPageAction
    | SetSortAction<SortField>
    | SetFilterAction<FilterField>;

const reducer = <SortField, FilterField>(
    state: State<SortField, FilterField>,
    action: Action<SortField, FilterField>
): State<SortField, FilterField> => {
    switch (action.type) {
        case 'setPage':
            return { ...state, page: action.page };

        case 'setPageSize':
            return { ...state, page: 1, pageSize: action.pageSize };

        case 'setSort':
            return { ...state, page: 1, sort: action.sortBy };

        case 'setFilter':
            return { ...state, page: 1, filter: action.filteringRule };

        default:
            return extendedReducer(state, action);
    }
};

type InitialStateReducer<SortField, FilterField> = Partial<State<SortField, FilterField>> & {
    sort: SortingRule<SortField>;
    filter: FilteringRule<FilterField>;
};

type ListReducer<SortField, FilterField> = Reducer<State<SortField, FilterField>, Action<SortField, FilterField>>;

export const defaultInitialState = {
    page: 1,
    pageSize: 10,
};

export const useListReducer = <SortField, FilterField>(initialState?: InitialStateReducer<SortField, FilterField>) =>
    useReducer<ListReducer<SortField, FilterField>>(reducer, {
        ...defaultInitialState,
        ...initialState,
    });
